import { differenceInHours, differenceInMinutes, isAfter, isValid, parseISO } from 'date-fns';
import { IconNameT, MIME_TYPES } from '@cad-arc';
import { UserExport } from '@mark43/cad-api';
import { isPresignedUrlExport } from '../common/helpers/files';
import { FILE_WEB_SERVER_PATH_TTL_MINUTES } from './constants';

export function getIconForExport(exportItem: UserExport): IconNameT {
    if (exportItem.failedPrint) {
        return 'Alert';
    } else if (exportItem.file === undefined) {
        return 'Document';
    } else if (exportItem.file.fileType === 'ZIP') {
        return 'DocumentZip';
    } else if (exportItem.file.fileCategory === 'PDF') {
        return 'PDFDocument';
    } else if (exportItem.file.fileCategory === 'IMAGE') {
        return 'Image';
    } else {
        return 'Document';
    }
}

export function getExportFileType(exportItem: UserExport): string {
    if (exportItem.file === undefined) {
        return MIME_TYPES.txt;
    }

    switch (exportItem?.file?.fileCategory) {
        case 'PDF':
            return MIME_TYPES.pdf;
        case 'IMAGE':
            return MIME_TYPES.png;
        default:
            return MIME_TYPES.zip;
    }
}

export function getExportExpiration(
    exportItem: UserExport,
    mediaAttachmentsEnabled: boolean
): string {
    if (exportItem.expirationDate === undefined) {
        return '';
    }

    const dateWhenExpired = new Date(exportItem.expirationDate);

    if (isAfter(new Date(), dateWhenExpired)) {
        return 'Expired';
    }

    // Check if this is a short-lived export (50 minutes)
    const usePresignedUrl = isPresignedUrlExport(exportItem.file, mediaAttachmentsEnabled);

    if (usePresignedUrl) {
        // For short-lived exports, show minutes remaining
        const createdDate = exportItem.file?.createdDateUtc || exportItem.createdDateUtc;
        if (!createdDate) {
            return 'Expires in 50 minutes';
        }

        const createdDateParsed = parseISO(createdDate);
        if (!isValid(createdDateParsed)) {
            return 'Expires in 50 minutes';
        }
        const now = new Date();

        const minutesOld = differenceInMinutes(now, createdDateParsed);
        const minutesRemaining = FILE_WEB_SERVER_PATH_TTL_MINUTES - minutesOld;
        if (minutesRemaining <= 0) {
            return 'Expired';
        }

        return `Expires in ${minutesRemaining} ${minutesRemaining === 1 ? 'minute' : 'minutes'}`;
    } else {
        // For long-lived exports, show hours (existing behavior)
        const hoursTilExpiration = Math.min(
            differenceInHours(new Date(exportItem.expirationDate), new Date()),
            11
        );

        return `Expires in ${hoursTilExpiration} ${hoursTilExpiration === 1 ? 'hour' : 'hours'}`;
    }
}
